import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const VideoGallerySection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const videos = data?.videos || [];
  const layout = data?.layout || 'grid';
  const [activeVideo, setActiveVideo] = useState(null);
  
  if (videos.length === 0) return null;

  // Extract YouTube video ID
  const getYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match?.[1];
  };

  // Extract YouTube playlist ID
  const getPlaylistId = (url) => {
    if (!url) return null;
    const match = url.match(/[?&]list=([^&]+)/);
    return match?.[1];
  };

  // Extract Vimeo video ID
  const getVimeoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:vimeo\.com\/)(\d+)/);
    return match?.[1];
  };

  // Get embed URL based on platform
  const getEmbedUrl = (video) => {
    const url = video.url || video.youtube_url;
    
    if (video.platform === 'youtube_playlist' || video.playlistId) {
      const playlistId = video.playlistId || getPlaylistId(url);
      return `https://www.youtube.com/embed/videoseries?list=${playlistId}`;
    }
    
    if (video.platform === 'vimeo' || url?.includes('vimeo')) {
      const vimeoId = getVimeoId(url);
      return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : null;
    }
    
    const youtubeId = getYoutubeId(url);
    return youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : null;
  };

  // Get thumbnail URL
  const getThumbnail = (video) => {
    const url = video.url || video.youtube_url;
    
    if (video.platform === 'vimeo' || url?.includes('vimeo')) {
      const vimeoId = getVimeoId(url);
      return vimeoId ? `https://vumbnail.com/${vimeoId}.jpg` : null;
    }
    
    const youtubeId = getYoutubeId(url);
    return youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : null;
  };

  // Grid Layout
  const renderGridLayout = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {videos.map((video, idx) => {
        const embedUrl = getEmbedUrl(video);
        const thumbnail = getThumbnail(video);
        const isPlaylist = video.platform === 'youtube_playlist';
        
        if (!embedUrl) return null;
        
        return (
          <AnimatedSection key={video.id || idx} animation="fadeUp" delay={idx * 100}>
            <div 
              className={cn("relative group cursor-pointer rounded-xl overflow-hidden", t.border, "border")}
              onClick={() => setActiveVideo(video)}
            >
              <div className="aspect-video relative">
                {thumbnail ? (
                  <img src={thumbnail} alt={video.title} className="w-full h-full object-cover" />
                ) : (
                  <div className={cn("w-full h-full flex items-center justify-center", t.bg)}>
                    <Play className="w-12 h-12" style={{ color: primaryColor }} />
                  </div>
                )}
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-8 h-8 text-black ml-1" />
                  </div>
                </div>
                {isPlaylist && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 rounded text-xs text-white font-medium">
                    PLAYLIST
                  </div>
                )}
              </div>
              {video.title && (
                <div className={cn("p-4", t.bg)}>
                  <h4 className={cn("font-semibold truncate", t.text)}>{video.title}</h4>
                  {video.description && (
                    <p className={cn("text-sm mt-1 line-clamp-2", t.textMuted)}>{video.description}</p>
                  )}
                </div>
              )}
            </div>
          </AnimatedSection>
        );
      })}
    </div>
  );

  // List Layout
  const renderListLayout = () => (
    <div className="space-y-6 max-w-4xl mx-auto">
      {videos.map((video, idx) => {
        const embedUrl = getEmbedUrl(video);
        if (!embedUrl) return null;
        
        return (
          <AnimatedSection key={video.id || idx} animation="slideRight" delay={idx * 100}>
            <div className={cn("rounded-xl overflow-hidden border", t.border, t.bg)}>
              <div className="aspect-video">
                <iframe
                  src={embedUrl}
                  title={video.title || `Video ${idx + 1}`}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
              {video.title && (
                <div className="p-4">
                  <h4 className={cn("font-semibold", t.text)}>{video.title}</h4>
                  {video.description && (
                    <p className={cn("text-sm mt-1", t.textMuted)}>{video.description}</p>
                  )}
                </div>
              )}
            </div>
          </AnimatedSection>
        );
      })}
    </div>
  );

  // Featured Layout - first video large, rest in grid
  const renderFeaturedLayout = () => {
    const [featured, ...rest] = videos;
    const featuredEmbed = featured ? getEmbedUrl(featured) : null;
    
    return (
      <div className="space-y-8">
        {featured && featuredEmbed && (
          <AnimatedSection animation="fadeUp">
            <div className={cn("rounded-2xl overflow-hidden border", t.border)}>
              <div className="aspect-video">
                <iframe
                  src={featuredEmbed}
                  title={featured.title || 'Featured Video'}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
              {featured.title && (
                <div className={cn("p-6", t.bg)}>
                  <h3 className={cn("text-xl font-bold", t.text)}>{featured.title}</h3>
                  {featured.description && (
                    <p className={cn("mt-2", t.textMuted)}>{featured.description}</p>
                  )}
                </div>
              )}
            </div>
          </AnimatedSection>
        )}
        
        {rest.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rest.map((video, idx) => {
              const thumbnail = getThumbnail(video);
              return (
                <AnimatedSection key={video.id || idx} animation="scale" delay={idx * 80}>
                  <div 
                    className={cn("relative group cursor-pointer rounded-lg overflow-hidden border", t.border)}
                    onClick={() => setActiveVideo(video)}
                  >
                    <div className="aspect-video relative">
                      {thumbnail ? (
                        <img src={thumbnail} alt={video.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className={cn("w-full h-full flex items-center justify-center", t.bg)}>
                          <Play className="w-8 h-8" style={{ color: primaryColor }} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-6 h-6 text-black ml-0.5" />
                        </div>
                      </div>
                    </div>
                    {video.title && (
                      <div className={cn("p-3", t.bg)}>
                        <h4 className={cn("font-medium text-sm truncate", t.text)}>{video.title}</h4>
                      </div>
                    )}
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Video Modal
  const renderVideoModal = () => {
    if (!activeVideo) return null;
    const embedUrl = getEmbedUrl(activeVideo);
    if (!embedUrl) return null;

    return (
      <div 
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
        onClick={() => setActiveVideo(null)}
      >
        <button 
          className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          onClick={() => setActiveVideo(null)}
        >
          <X className="w-6 h-6" />
        </button>
        <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
          <div className="aspect-video rounded-xl overflow-hidden">
            <iframe
              src={embedUrl + '?autoplay=1'}
              title={activeVideo.title || 'Video'}
              className="w-full h-full"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
          {activeVideo.title && (
            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white">{activeVideo.title}</h3>
              {activeVideo.description && (
                <p className="text-white/70 mt-2">{activeVideo.description}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section id="videos" className={cn("py-24 px-4", t.card)}>
      <div className="max-w-6xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>
            Watch
          </span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.title || 'Videos'}
          </h2>
        </AnimatedSection>
        
        {layout === 'grid' && renderGridLayout()}
        {layout === 'list' && renderListLayout()}
        {layout === 'featured' && renderFeaturedLayout()}
      </div>
      
      {renderVideoModal()}
    </section>
  );
};


